import {Component, OnInit, DestroyRef, inject, OnDestroy} from '@angular/core';
import { Alert, AlertType, ContentTableModule, DropdownModule } from '@michelin/theme';
import { SharedModule } from '../../shared/shared.module';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { finalize } from 'rxjs/operators';
import { MatTableDataSource } from "@angular/material/table";
import { ContextService } from '../../shared/services/context.service';
import { Seasonality } from "../../shared/models/seasonality";
import { SeasonalityService } from "../../shared/services/seasonality.service";
import { FormArray, FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  SEASONALITY_TYPE_YEAR,
  SEASONALITY_TYPE_AVERAGE,
  SEASONALITY_TYPE_CALCULATED_INDEX,
  SEASONALITY_TYPE_PROPOSED_INDEX
} from 'src/app/shared/constants/SeasonalityConstants';
import { ModificationButtonsComponent } from "../../shared/components/modification-buttons/modification-buttons.component";
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from "@angular/material/chips";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { AlertService } from '@michelin/theme';
import {MatDialog} from "@angular/material/dialog";
import {SaveRecalcConfirmDialogComponent} from "../../shared/components/save-recalc-confirm-dialog/save-recalc-confirm-dialog.component";
import {IModif} from "../../shared/models/IModif";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: 'app-seasonality',
  standalone: true,
  imports: [
    ContentTableModule,
    SharedModule,
    DropdownModule,
    ModificationButtonsComponent,
    FormsModule,
    ReactiveFormsModule,
    MatTooltipModule,
    MatChipsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: 'seasonality.component.html',
  styleUrls: ['./seasonality.component.scss']
})
export class SeasonalityComponent implements OnInit, IModif, OnDestroy {

  private seasonalityService = inject(SeasonalityService);
  private destroyRef = inject(DestroyRef);

  dataSourceYear = new MatTableDataSource<Seasonality>();
  dataSourceIndex = new MatTableDataSource<Seasonality>();

  displayedColumns = [
    'year',
    ...Array.from({ length: 12 }, (_, i) => `m${i + 1}`),
    'average',
    'action'
  ];

  monthKeys = [
    'sites.seasonality.months.jan',
    'sites.seasonality.months.feb',
    'sites.seasonality.months.mar',
    'sites.seasonality.months.apr',
    'sites.seasonality.months.may',
    'sites.seasonality.months.jun',
    'sites.seasonality.months.jul',
    'sites.seasonality.months.aug',
    'sites.seasonality.months.sep',
    'sites.seasonality.months.oct',
    'sites.seasonality.months.nov',
    'sites.seasonality.months.dec',
  ];

  sizeCtrl = new FormControl<string[] | null>([]);
  sizes: string[] = [];
  isModificationsPending = false;
  isProposedOutOfBounds = false;

  protected readonly SEASONALITY_TYPE_CALCULATED_INDEX = SEASONALITY_TYPE_CALCULATED_INDEX;
  protected readonly SEASONALITY_TYPE_PROPOSED_INDEX = SEASONALITY_TYPE_PROPOSED_INDEX;
  protected readonly SEASONALITY_TYPE_YEAR = SEASONALITY_TYPE_YEAR;
  protected readonly SEASONALITY_TYPE_AVERAGE = SEASONALITY_TYPE_AVERAGE;

  shouldAnimate = false;
  siteId!: number;
  isLoading = false;
  saveIsLoading = false;

  // Form for line proposedIndex
  proposedCtrl = new FormArray<FormControl<number | null>>([]);

  // Snapshots for change detection
  private initialSizes: string[] = [];
  private initialProposedMonths: (number | null)[] = [];

  constructor(private context: ContextService, private alertService: AlertService, private translate: TranslateService) {
    context.register(this);
  }

  getModifStatus(): boolean {
    return this.isModificationsPending;
  }

  readonly dialog = inject(MatDialog);

  onConfirmSaveAndRecalculate(): void {
    const dialogRef = this.dialog.open(SaveRecalcConfirmDialogComponent);
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.saveAndRecalculateForecast();
      }
    });
  }

  private get proposedRow(): Seasonality | undefined {
    return this.dataSourceIndex.data.find(r => r.type === SEASONALITY_TYPE_PROPOSED_INDEX);
  }

  ngOnInit() {
    this.context.siteId$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(siteId => {
        if (siteId) {
          this.siteId = siteId;
          this.loadSeasonality();
        }
      });

    this.sizeCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshPendingFlag());
  }

  ngOnDestroy(): void {
    this.context.unRegister(this);
  }

  /**
   * Load seasonality data from the service
   */
  loadSeasonality() {
    this.isLoading = true;
    this.seasonalityService
      .getSeasonalityAndSizes(this.siteId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => { this.isLoading = false; })
      )
      .subscribe(response => {
        const rows = response.seasonalityResponse.rows.map(row => { this.roundRowValues(row); return row; });
        this.sizes = response.allSizes;

        // Initialize sizes control
        this.sizeCtrl.setValue(response.selectedSizes && response.selectedSizes.length ? response.selectedSizes : []);

        const averageRow = rows.find(r => r.type === SEASONALITY_TYPE_AVERAGE);
        const yearRows = rows.filter(r => r.type === SEASONALITY_TYPE_YEAR);
        const indexRows = rows.filter(
          row => row.type === SEASONALITY_TYPE_CALCULATED_INDEX || row.type === SEASONALITY_TYPE_PROPOSED_INDEX
        );

        if (averageRow) {
          averageRow.months = averageRow.months.map(m => m !== null ? Math.round(m) : null);
          averageRow.average = averageRow.average !== null ? Math.round(averageRow.average) : null;
        }

        this.dataSourceYear.data = averageRow ? [averageRow, ...yearRows] : yearRows;
        this.dataSourceIndex.data = indexRows;

        // Initialize proposed form
        const proposedRow = this.proposedRow;
        if (proposedRow) {
          proposedRow.months = this.normalizeEmptyMonths(proposedRow.months);
          this.computeAverage(proposedRow);
          this.buildProposedFormFrom(proposedRow);
        }

        this.dataSourceIndex.data.forEach(row => this.computeAverage(row));

        // Initial Snapshot for change detection
        this.initialSizes = (this.sizeCtrl.value ?? []).slice().sort();
        this.initialProposedMonths = this.getProposedMonths().slice();

        this.refreshPendingFlag();
      });
  }

  /**
   * Build the proposed FormArray from a Seasonality row
   * @param row
   * @private
   */
  private buildProposedFormFrom(row: Seasonality): void {
    const months = this.normalizeEmptyMonths(row.months);
    this.proposedCtrl.clear({ emitEvent: false });

    // Create FormControls for each month with validators
    months.forEach(month => {
      this.proposedCtrl.push(
        new FormControl<number | null>(month, {
          nonNullable: false,
          validators: [Validators.min(0), Validators.max(999)]
        }),
        { emitEvent: false }
      );
    });

    // Subscribe to changes to handle rounding and updates
    this.proposedCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(values => {
        const rounded = values.map(value =>
          value === null || value === undefined || Number.isNaN(Number(value)) ? value as any : Math.round(Number(value))
        );

        // Update only if there are changes after rounding
        const needPatch = rounded.some((value, nombre) => value !== values[nombre]);
        if (needPatch) this.proposedCtrl.patchValue(rounded, { emitEvent: false });

        if (this.proposedRow) {
          this.proposedRow.months = rounded as (number | null)[];
          this.computeAverage(this.proposedRow);
        }

        this.refreshPendingFlag();
      });
  }

  /**
   * Blur event if a month input is left
   * @param index
   */
  onMonthBlur(index: number): void {
    const ctrl = this.proposedCtrl.at(index) as FormControl<number | null>;
    const value = ctrl.value;
    const nombre = Number(value);

    ctrl.setValue(
      value === null || value === undefined || Number.isNaN(nombre) ? 100 : Math.max(0, Math.min(999, Math.round(nombre)))
    );
  }

  /**
   * Normalize empty months to default values
   * @param months
   * @private
   */
  private normalizeEmptyMonths(months: (number | null)[] | undefined): (number | null)[] {
    if (!months || months.length === 0 || months.every(month => month === null || month === undefined)) {
      return Array(12).fill(100);
    }
    return months.slice();
  }

  removeSize(size: string) {
    const updated = this.sizeCtrl.value?.filter(s => s !== size) ?? [];
    this.sizeCtrl.setValue(updated);
  }

  private computeAverage(row: Seasonality): void {
    const values = row.months.filter((month): month is number => month !== null);
    row.average = values.length ? Math.round(values.reduce((a, b) => a + b, 0) / values.length) : null;
  }

  /**
   * Toggle inclusion/exclusion of a year row
   * @param row
   */
  toggleRow(row: Seasonality) {
    row.included = !row.included;
    this.refreshPendingFlag();
    this.recomputeSeasonality();
  }

  /**
   * Recompute seasonality based on included years
   */
  recomputeSeasonality() {
    const includedYears = this.dataSourceYear.data
      .filter(row => row.type === SEASONALITY_TYPE_YEAR && row.included)
      .map(row => row.year);

    this.seasonalityService
      .recomputeSeasonality(this.siteId, includedYears)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        this.updateTables(response.rows);
        this.refreshPendingFlag();
      });
  }

  /**
   * Copy calculated index values to proposed index
   */
  copyCalculatedToProposed(): void {
    const calculatedRow = this.dataSourceIndex.data.find(row => row.type === SEASONALITY_TYPE_CALCULATED_INDEX);
    if (!calculatedRow) return;

    const copied = this.normalizeEmptyMonths(calculatedRow.months).map(month => (month !== null ? Math.round(month) : null));
    this.proposedCtrl.patchValue(copied, { emitEvent: true });
  }

  /**
   * Round month values and average
   * @param row
   * @private
   */
  private roundRowValues(row: Seasonality): void {
    if (row.months) {
      row.months = row.months.map(m => m !== null && m !== undefined ? Math.round(Number(m)) : m);
    }
    if (row.average !== null && row.average !== undefined) {
      row.average = Math.round(Number(row.average));
    }
  }

  /**
   * Update data tables for yearly and index seasonality
   * @param rows
   * @private
   */
  private updateTables(rows: Seasonality[]) {
    rows.forEach(row => this.roundRowValues(row));

    const averageRow = rows.find(row => row.type === SEASONALITY_TYPE_AVERAGE);
    const yearRows = rows.filter(row => row.type === SEASONALITY_TYPE_YEAR);
    const newIndexRows = rows.filter(
      row => row.type === SEASONALITY_TYPE_CALCULATED_INDEX || row.type === SEASONALITY_TYPE_PROPOSED_INDEX
    );

    // Preserve existing proposed months
    const existingProposed = this.proposedRow?.months?.slice();
    this.dataSourceYear.data = averageRow ? [averageRow, ...yearRows] : yearRows;
    this.dataSourceIndex.data = newIndexRows;

    const proposed = this.proposedRow;
    if (proposed) {
      proposed.months = existingProposed ?? this.normalizeEmptyMonths(proposed.months);
      this.computeAverage(proposed);
      this.proposedCtrl.patchValue(proposed.months, { emitEvent: false });
    }

    this.dataSourceIndex.data.forEach(row => this.computeAverage(row));
    this.shouldAnimate = true;
  }

  /**
   * Check if the average of proposed index is out of range
   * @param row
   */
  isAverageOutOfRange(row: Seasonality): boolean {
    return row.type === SEASONALITY_TYPE_PROPOSED_INDEX && row.average !== null && (row.average < 100 || row.average > 103);
  }

  /**
   * Reset all modifications to initial state
   */
  resetModifications(): void {
    this.shouldAnimate = false;
    // Reset inclusion of years
    this.dataSourceYear.data
      .filter(row => row.type === SEASONALITY_TYPE_YEAR)
      .forEach(row => row.included = true);

    // Reset sizes
    this.sizeCtrl.setValue(this.initialSizes.slice());

    // Reset proposed months
    if (this.proposedCtrl.length === 12) {
      this.proposedCtrl.patchValue(this.initialProposedMonths, { emitEvent: true });
    }

    this.recomputeSeasonality();
    this.refreshPendingFlag();
  }

  /**
   * Update the snapshot of current sizes and proposed months
   */
  updateSnapshot(): void {
    this.initialSizes = (this.sizeCtrl.value ?? []).slice().sort();
    this.initialProposedMonths = this.getProposedMonths().slice();
    this.refreshPendingFlag();
  }

  /**
   * Persist seasonality changes for method save and save & recalc
   * @param withRecalc
   * @private
   */
  private persistSeasonality(withRecalc: boolean): void {
    const proposedRow = this.proposedRow;
    if (!proposedRow) return;

    if (this.isAverageOutOfRange(proposedRow)) {
      this.alertService.showAlert({
        type: AlertType.critical,
        title: this.translate.instant('sites.seasonality.alerts.outOfRange.title'),
        content: this.translate.instant('sites.seasonality.alerts.outOfRange.description'),
      } as Alert);
      return;
    }

    const payload = {
      siteId: this.siteId,
      sizes: this.sizeCtrl.value ?? [],
      months: this.getProposedMonths()
    };

    this.saveIsLoading = true;
    this.seasonalityService
      .saveSeasonality(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.updateSnapshot(),
        error: () => { this.saveIsLoading = false; },
        complete: () => {
          this.alertService.showAlert({
            type: AlertType.success,
            title: this.translate.instant('sites.seasonality.alerts.saveSeasonality.title'),
            content: this.translate.instant('sites.seasonality.alerts.saveSeasonality.description'),
          } as Alert);
          this.saveIsLoading = false;
          this.isLoading = false;

          if (withRecalc) {
            this.saveIsLoading = false;
            this.seasonalityService
              .recalculateForecast(this.siteId)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                complete: () => {
                  this.alertService.showAlert({
                    type: AlertType.success,
                    title: this.translate.instant('sites.seasonality.alerts.recalculateForecast.title'),
                    content: this.translate.instant('sites.seasonality.alerts.recalculateForecast.description'),
                  } as Alert);
                  this.saveIsLoading = false;
                },
                error: err => {
                  this.saveIsLoading = false;
                  if (err.status) {
                    this.alertService.showAlert({
                      type: AlertType.critical,
                      title: this.translate.instant('sites.seasonality.alerts.recalculateForecast.errorTitle'),
                      content: err.error
                    } as Alert);
                    return;
                  }
                }
              });
            }
        }
      });
  }

  saveModifications(): void {
    this.persistSeasonality(false);
  }

  saveAndRecalculateForecast(): void {
    this.persistSeasonality(true);
  }

  /**
   * Refresh the pending modifications flag
   * @private
   */
  private refreshPendingFlag(): void {
    const currentSizes = (this.sizeCtrl.value ?? []).slice().sort();
    const currentProposedMonths = this.getProposedMonths();

    const sizesChanged = !this.arraysEqual(currentSizes, this.initialSizes);
    const monthsChanged = !this.arraysEqual(currentProposedMonths, this.initialProposedMonths);

    this.isProposedOutOfBounds =
      this.proposedCtrl.invalid ||
      currentProposedMonths.some(month => month !== null && (month < 0 || month > 999));

    this.isModificationsPending = sizesChanged || monthsChanged;
  }

  /**
   * Compare two arrays for equality
   * @param current
   * @param initial
   * @private
   */
  private arraysEqual(current: (number | string | null)[], initial: (number | string | null)[]): boolean {
    if (current.length !== initial.length) return false;
    for (let i = 0; i < current.length; i++) {
      if (current[i] !== initial[i]) return false;
    }
    return true;
  }

  private getProposedMonths(): (number | null)[] {
    if (this.proposedCtrl.length !== 12) return this.proposedRow?.months ?? [];
    return this.proposedCtrl.controls.map(control => control.value);
  }

}
